import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  useThreatIntelSummary,
  useThreatIntelSearch,
  useThreatIntelFeeds,
} from "@/hooks/use-api";

const TYPE_LABELS: Record<string, string> = {
  ip: "IP Address",
  domain: "Domain",
  file_hash: "File Hash",
  url: "URL",
};

const TYPE_COLORS: Record<string, string> = {
  ip: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  domain: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  file_hash: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  url: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
};

export default function ThreatIntelPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: summary, isLoading: summaryLoading } = useThreatIntelSummary();
  const { data: searchResults, isLoading: searchLoading } = useThreatIntelSearch(searchQuery);
  const { data: feedsData } = useThreatIntelFeeds();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Threat Intelligence</h1>
        <p className="text-muted-foreground">
          IOC feeds, indicator search, and threat intelligence overview
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Indicators</CardDescription>
            <CardTitle className="text-3xl">
              {summaryLoading ? "..." : (summary?.total_indicators ?? 0).toLocaleString()}
            </CardTitle>
          </CardHeader>
        </Card>
        {Object.entries(TYPE_LABELS).map(([key, label]) => (
          <Card key={key}>
            <CardHeader className="pb-2">
              <CardDescription>{label}s</CardDescription>
              <CardTitle className="text-3xl">
                {summaryLoading ? "..." : (summary?.by_type?.[key] ?? 0).toLocaleString()}
              </CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Feed health */}
      <Card>
        <CardHeader>
          <CardTitle>Active Feeds</CardTitle>
          <CardDescription>Threat intelligence sources currently configured</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {feedsData?.feeds?.map((feed) => (
              <div
                key={feed.name}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div>
                  <p className="font-medium text-sm">{feed.display_name}</p>
                  <div className="flex gap-1 mt-1">
                    {feed.types.map((t) => (
                      <span
                        key={t}
                        className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${TYPE_COLORS[t] || ""}`}
                      >
                        {TYPE_LABELS[t] || t}
                      </span>
                    ))}
                  </div>
                </div>
                {feed.requires_key && (
                  <Badge variant="outline" className="text-xs">
                    API Key
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Indicator Search</CardTitle>
          <CardDescription>
            Search for an IP, domain, hash, or URL across all feeds
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Search indicator (e.g. 1.2.3.4, evil.com, SHA256 hash...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-lg"
          />

          {searchLoading && searchQuery.length >= 3 && (
            <p className="mt-4 text-sm text-muted-foreground">Searching...</p>
          )}

          {searchResults && searchResults.results.length > 0 && (
            <div className="mt-4 rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2 text-left font-medium">Indicator</th>
                    <th className="px-4 py-2 text-left font-medium">Type</th>
                    <th className="px-4 py-2 text-left font-medium">Categories</th>
                    <th className="px-4 py-2 text-left font-medium">Sources</th>
                    <th className="px-4 py-2 text-left font-medium">Confidence</th>
                    <th className="px-4 py-2 text-left font-medium">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.results.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-4 py-2 font-mono text-xs break-all">
                        {r.indicator}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${TYPE_COLORS[r.indicator_type] || ""}`}
                        >
                          {TYPE_LABELS[r.indicator_type] || r.indicator_type}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {r.categories.map((c) => (
                          <Badge key={c} variant="secondary" className="mr-1 text-xs">
                            {c}
                          </Badge>
                        ))}
                      </td>
                      <td className="px-4 py-2 text-xs">{r.sources.join(", ")}</td>
                      <td className="px-4 py-2">{r.confidence}%</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {r.last_seen ? new Date(r.last_seen).toLocaleDateString() : "\u2014"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {searchResults && searchResults.results.length === 0 && searchQuery.length >= 3 && (
            <p className="mt-4 text-sm text-muted-foreground">
              No matches found for &quot;{searchQuery}&quot;
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
